import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, TrendDown, CaretDown, Globe, UsersThree, Trophy } from "@phosphor-icons/react";
import api from "@/lib/api";
import { money as fmt } from "@/lib/format";

const money = (v, cur) => fmt(v, cur, { decimals: 2 });
import { Card, SectionTitle } from "@/components/riq";

const useBasket = (receiptId) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/prices/basket/${receiptId || "latest"}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [receiptId]);
  return data;
};

export const HomeBasketCard = () => {
  const data = useBasket();
  const navigate = useNavigate();
  if (!data || !data.receipt || !data.stores.length) return null;
  const { receipt: r, best, stores } = data;
  const cur = r.currency || "ZAR";
  return (
    <Card data-testid="home-cheapest-basket" onClick={() => navigate(`/receipts/${r.id}#basket`)}
      className="cursor-pointer hover:-translate-y-0.5 transition-transform">
      <div className="flex items-start gap-4">
        <div className={`h-11 w-11 shrink-0 rounded-2xl flex items-center justify-center ${best ? "bg-emerald-100 text-emerald-700" : "bg-riq-primary/10 text-riq-primary"}`}>
          {best ? <TrendDown size={22} weight="bold" /> : <Trophy size={22} weight="duotone" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cheapest basket</div>
          <div data-testid="home-basket-headline" className="mt-0.5 font-semibold text-neutral-800">
            Your last shop at {r.store}: {money(r.total, cur)}
          </div>
          {best ? (
            <div data-testid="home-basket-best" className="text-sm text-neutral-600 mt-0.5">
              Cheapest: <b className="text-emerald-700">{best.store} {money(best.store_cost, cur)}</b>, save {money(best.savings, cur)}
              <span className="text-xs text-muted-foreground"> · {best.matched} of {best.total_items} items</span>
            </div>
          ) : (
            <div data-testid="home-basket-best" className="text-sm text-neutral-600 mt-0.5">
              Nice! {r.store} beat {stores.length} other store{stores.length === 1 ? "" : "s"} on the items we could compare.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

function StoreRow({ s, cur, isBest, idx, defaultOpen }) {
  const [open, setOpen] = useState(isBest || defaultOpen);
  const pct = Math.round(s.coverage * 100);
  return (
    <div data-testid={`basket-store-${idx}`} className={`rounded-2xl border ${isBest ? "border-emerald-300 bg-emerald-50/50" : "border-riq-border"}`}>
      <button data-testid={`basket-store-toggle-${idx}`} onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-neutral-800">{s.store}</span>
            {isBest && <span className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Cheapest</span>}
          </div>
          <div className="text-xs text-muted-foreground">{s.matched} of {s.total_items} items ({pct}%)</div>
        </div>
        <div className="text-right">
          <div className="font-semibold tabular-nums">{money(s.store_cost, cur)}</div>
          <div className={`text-xs tabular-nums ${s.savings > 0 ? "text-emerald-700" : "text-red-600"}`}>
            {s.savings > 0 ? `save ${money(s.savings, cur)}` : `${money(-s.savings, cur)} more`}
          </div>
        </div>
        <CaretDown size={16} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-riq-border/70 px-4 py-2 space-y-1.5">
          {s.items.map((it, i) => (
            <div key={i} data-testid={`basket-store-${idx}-item-${i}`} className="flex items-center gap-2 text-sm">
              {it.source === "online" ? <Globe size={14} className="text-sky-600 shrink-0" /> : <UsersThree size={14} className="text-amber-600 shrink-0" />}
              <span className="min-w-0 flex-1 truncate" title={it.match}>{it.qty > 1 ? `${it.qty}× ` : ""}{it.item}</span>
              <span className="text-xs text-muted-foreground tabular-nums line-through">{money(it.your_price, cur)}</span>
              <span className={`w-20 text-right tabular-nums font-medium ${it.store_price < it.your_price ? "text-emerald-700" : "text-neutral-700"}`}>{money(it.store_price, cur)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const BasketBreakdown = ({ receiptId }) => {
  const data = useBasket(receiptId);
  const { hash } = useLocation();
  const ref = useRef(null);
  useEffect(() => {
    if (data && hash === "#basket" && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data, hash]);
  if (!data || !data.receipt || !data.stores.length) return null;
  const cur = data.receipt.currency || "ZAR";
  return (
    <div id="basket" ref={ref} data-testid="receipt-basket-breakdown" className="scroll-mt-20">
      <SectionTitle>
        <span className="flex items-center gap-2"><ShoppingCart size={18} weight="duotone" /> Cheapest basket</span>
      </SectionTitle>
      <Card className="space-y-3">
        <p className="text-xs text-muted-foreground">
          The same items at other stores, like-for-like, from community receipts (last {data.window_days} days) and online prices.
          Each store is only compared on the items it stocks.
        </p>
        {data.stores.map((s, i) => <StoreRow key={s.store} s={s} cur={cur} idx={i} isBest={data.best?.store === s.store} defaultOpen={i === 0} />)}
        <div className="flex gap-4 text-[11px] text-muted-foreground pt-1">
          <span className="flex items-center gap-1"><UsersThree size={12} className="text-amber-600" /> Community receipts</span>
          <span className="flex items-center gap-1"><Globe size={12} className="text-sky-600" /> Online price</span>
        </div>
      </Card>
    </div>
  );
};
